import {
  MAX_NEW_CARD_MEDIA_BYTES,
  NEW_CARD_MEDIA_REFERENCE_PREFIX,
  NEW_CARD_PHOTO_SCHEMA_VERSION,
  isNewCardPhotoMetadata,
  type NewCardPhotoMetadata,
} from "./new-card";

export const NEW_CARD_MEDIA_DATABASE = "convoful-owner-new-card-media-v1";
export const NEW_CARD_IMAGE_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
  "image/avif",
  "image/heic",
  "image/heif",
] as const;

interface StoredNewCardMedia {
  kind: "owner_new_card_media";
  schemaVersion: typeof NEW_CARD_PHOTO_SCHEMA_VERSION;
  ownerUserID: string;
  draftID: string;
  reference: string;
  mimeType: string;
  byteCount: number;
  sha256: string;
  image: Blob;
}

export interface NewCardMediaStore {
  stage(
    ownerUserID: string,
    draftID: string,
    image: Blob,
  ): Promise<NewCardPhotoMetadata>;
  commit(
    ownerUserID: string,
    draftID: string,
    metadata: NewCardPhotoMetadata,
  ): Promise<void>;
  abort(
    ownerUserID: string,
    draftID: string,
    metadata: NewCardPhotoMetadata,
  ): Promise<void>;
  load(
    ownerUserID: string,
    draftID: string,
    metadata: NewCardPhotoMetadata,
  ): Promise<Blob | null>;
  clearDraft(ownerUserID: string, draftID: string): Promise<void>;
  clearOwner(ownerUserID: string): Promise<void>;
  clearAll(): Promise<void>;
}

interface NewCardMediaStoreOptions {
  createID?: () => string;
  validateReadableImage?: (image: Blob) => Promise<void>;
}

export class MemoryNewCardMediaStore implements NewCardMediaStore {
  private readonly records = new Map<string, StoredNewCardMedia>();
  private readonly createID: () => string;
  private readonly validateReadableImage: (image: Blob) => Promise<void>;

  constructor(options: NewCardMediaStoreOptions = {}) {
    this.createID = options.createID ?? (() => crypto.randomUUID());
    this.validateReadableImage = options.validateReadableImage
      ?? assertReadableBrowserImage;
  }

  async stage(
    ownerUserID: string,
    draftID: string,
    image: Blob,
  ): Promise<NewCardPhotoMetadata> {
    const record = await newCardMediaRecord(
      ownerUserID,
      draftID,
      image,
      this.createID,
      this.validateReadableImage,
    );
    this.records.set(record.reference, cloneStoredMedia(record));
    return metadataFor(record);
  }

  async commit(
    ownerUserID: string,
    draftID: string,
    metadata: NewCardPhotoMetadata,
  ): Promise<void> {
    const record = this.records.get(metadata.reference);
    await validatedStoredMedia(
      record,
      ownerUserID,
      draftID,
      metadata,
      this.validateReadableImage,
    );
    const [owner, draft] = assertOwnerAndDraft(ownerUserID, draftID);
    for (const [reference, current] of this.records) {
      if (
        current.ownerUserID === owner
        && current.draftID === draft
        && reference !== metadata.reference
      ) {
        this.records.delete(reference);
      }
    }
  }

  async abort(
    ownerUserID: string,
    draftID: string,
    metadata: NewCardPhotoMetadata,
  ): Promise<void> {
    const record = this.records.get(metadata.reference);
    if (record === undefined) return;
    assertStoredMediaIdentity(record, ownerUserID, draftID, metadata);
    this.records.delete(metadata.reference);
  }

  async load(
    ownerUserID: string,
    draftID: string,
    metadata: NewCardPhotoMetadata,
  ): Promise<Blob | null> {
    assertOwnerAndDraft(ownerUserID, draftID);
    if (!isNewCardPhotoMetadata(metadata)) {
      throw new Error("The private photo reference is invalid.");
    }
    const record = this.records.get(metadata.reference);
    if (!record) return null;
    const stored = await validatedStoredMedia(
      record,
      ownerUserID,
      draftID,
      metadata,
      this.validateReadableImage,
    );
    return cloneBlob(stored.image);
  }

  async clearDraft(ownerUserID: string, draftID: string): Promise<void> {
    assertOwnerAndDraft(ownerUserID, draftID);
    for (const [reference, record] of this.records) {
      if (
        record.ownerUserID === ownerUserID.trim()
        && record.draftID === draftID.trim()
      ) {
        this.records.delete(reference);
      }
    }
  }

  async clearOwner(ownerUserID: string): Promise<void> {
    const owner = assertBoundedIdentity(ownerUserID, "owner");
    for (const [reference, record] of this.records) {
      if (record.ownerUserID === owner) this.records.delete(reference);
    }
  }

  async clearAll(): Promise<void> {
    this.records.clear();
  }
}

export class LocalNewCardMediaStore implements NewCardMediaStore {
  private static readonly objectStore = "media";
  private readonly createID: () => string;
  private readonly validateReadableImage: (image: Blob) => Promise<void>;

  constructor(
    private readonly factory: IDBFactory,
    private readonly databaseName = NEW_CARD_MEDIA_DATABASE,
    options: NewCardMediaStoreOptions = {},
  ) {
    this.createID = options.createID ?? (() => crypto.randomUUID());
    this.validateReadableImage = options.validateReadableImage
      ?? assertReadableBrowserImage;
  }

  async stage(
    ownerUserID: string,
    draftID: string,
    image: Blob,
  ): Promise<NewCardPhotoMetadata> {
    const record = await newCardMediaRecord(
      ownerUserID,
      draftID,
      image,
      this.createID,
      this.validateReadableImage,
    );
    await this.put(record);
    return metadataFor(record);
  }

  async commit(
    ownerUserID: string,
    draftID: string,
    metadata: NewCardPhotoMetadata,
  ): Promise<void> {
    const record = await this.get(metadata.reference);
    await validatedStoredMedia(
      record,
      ownerUserID,
      draftID,
      metadata,
      this.validateReadableImage,
    );
    const [owner, draft] = assertOwnerAndDraft(ownerUserID, draftID);
    await this.pruneDraft(owner, draft, metadata.reference);
  }

  async abort(
    ownerUserID: string,
    draftID: string,
    metadata: NewCardPhotoMetadata,
  ): Promise<void> {
    const record = await this.get(metadata.reference);
    if (record === undefined) return;
    assertStoredMediaIdentity(record, ownerUserID, draftID, metadata);
    await this.delete(metadata.reference);
  }

  async load(
    ownerUserID: string,
    draftID: string,
    metadata: NewCardPhotoMetadata,
  ): Promise<Blob | null> {
    assertOwnerAndDraft(ownerUserID, draftID);
    if (!isNewCardPhotoMetadata(metadata)) {
      throw new Error("The private photo reference is invalid.");
    }
    const record = await this.get(metadata.reference);
    if (record === undefined) return null;
    try {
      const stored = await validatedStoredMedia(
        record,
        ownerUserID,
        draftID,
        metadata,
        this.validateReadableImage,
      );
      return cloneBlob(stored.image);
    } catch (error) {
      await this.delete(metadata.reference).catch(() => undefined);
      throw error;
    }
  }

  async clearDraft(ownerUserID: string, draftID: string): Promise<void> {
    const [owner, draft] = assertOwnerAndDraft(ownerUserID, draftID);
    const records = await this.getAll();
    await Promise.all(records.flatMap((record) => (
      isStoredNewCardMedia(record)
      && record.ownerUserID === owner
      && record.draftID === draft
        ? [this.delete(record.reference)]
        : []
    )));
  }

  async clearOwner(ownerUserID: string): Promise<void> {
    const owner = assertBoundedIdentity(ownerUserID, "owner");
    const records = await this.getAll();
    await Promise.all(records.flatMap((record) => (
      isStoredNewCardMedia(record) && record.ownerUserID === owner
        ? [this.delete(record.reference)]
        : []
    )));
  }

  async clearAll(): Promise<void> {
    await this.withStore("readwrite", (store) => store.clear());
  }

  private async pruneDraft(
    ownerUserID: string,
    draftID: string,
    retainedReference: string,
  ): Promise<void> {
    const records = await this.getAll();
    await Promise.all(records.flatMap((record) => (
      isStoredNewCardMedia(record)
      && record.ownerUserID === ownerUserID
      && record.draftID === draftID
      && record.reference !== retainedReference
        ? [this.delete(record.reference)]
        : []
    )));
  }

  private async put(record: StoredNewCardMedia): Promise<void> {
    await this.withStore(
      "readwrite",
      (store) => store.put(record, record.reference),
    );
  }

  private get(reference: string): Promise<unknown> {
    return this.withStore("readonly", (store) => store.get(reference));
  }

  private getAll(): Promise<unknown[]> {
    return this.withStore("readonly", (store) => store.getAll());
  }

  private async delete(reference: string): Promise<void> {
    await this.withStore("readwrite", (store) => store.delete(reference));
  }

  private async withStore<T>(
    mode: IDBTransactionMode,
    request: (store: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> {
    const database = await this.open();
    try {
      const transaction = database.transaction(
        LocalNewCardMediaStore.objectStore,
        mode,
      );
      const completion = indexedDBTransaction(transaction);
      const requestResult = indexedDBRequest(
        request(transaction.objectStore(LocalNewCardMediaStore.objectStore)),
      );
      const [result] = await Promise.all([requestResult, completion]);
      return result;
    } finally {
      database.close();
    }
  }

  private async open(): Promise<IDBDatabase> {
    const request = this.factory.open(this.databaseName, 1);
    request.addEventListener("upgradeneeded", () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(
        LocalNewCardMediaStore.objectStore,
      )) {
        database.createObjectStore(LocalNewCardMediaStore.objectStore);
      }
    });
    return indexedDBRequest(request);
  }
}

async function newCardMediaRecord(
  ownerUserID: string,
  draftID: string,
  image: Blob,
  createID: () => string,
  validateReadableImage: (image: Blob) => Promise<void>,
): Promise<StoredNewCardMedia> {
  const [owner, draft] = assertOwnerAndDraft(ownerUserID, draftID);
  assertAllowedImage(image);
  await validateReadableImage(image);
  const reference = `${NEW_CARD_MEDIA_REFERENCE_PREFIX}${createID()}`;
  const sha256 = await sha256Hex(image);
  const record: StoredNewCardMedia = {
    kind: "owner_new_card_media",
    schemaVersion: NEW_CARD_PHOTO_SCHEMA_VERSION,
    ownerUserID: owner,
    draftID: draft,
    reference,
    mimeType: image.type.toLowerCase(),
    byteCount: image.size,
    sha256,
    image: cloneBlob(image),
  };
  if (!isNewCardPhotoMetadata(metadataFor(record))) {
    throw new Error("A private photo reference could not be created.");
  }
  return record;
}

async function validatedStoredMedia(
  value: unknown,
  ownerUserID: string,
  draftID: string,
  metadata: NewCardPhotoMetadata,
  validateReadableImage: (image: Blob) => Promise<void>,
): Promise<StoredNewCardMedia> {
  const record = assertStoredMediaIdentity(
    value,
    ownerUserID,
    draftID,
    metadata,
  );
  assertAllowedImage(record.image);
  const digest = await sha256Hex(record.image);
  if (digest !== metadata.sha256) {
    throw new Error("The private photo failed its integrity check.");
  }
  await validateReadableImage(record.image);
  return record;
}

function assertStoredMediaIdentity(
  value: unknown,
  ownerUserID: string,
  draftID: string,
  metadata: NewCardPhotoMetadata,
): StoredNewCardMedia {
  const [owner, draft] = assertOwnerAndDraft(ownerUserID, draftID);
  if (
    !isNewCardPhotoMetadata(metadata)
    || !isStoredNewCardMedia(value)
    || value.ownerUserID !== owner
    || value.draftID !== draft
    || JSON.stringify(metadataFor(value)) !== JSON.stringify(metadata)
  ) {
    throw new Error("The private photo does not belong to this draft.");
  }
  return value;
}

function isStoredNewCardMedia(value: unknown): value is StoredNewCardMedia {
  return typeof value === "object"
    && value !== null
    && !Array.isArray(value)
    && "kind" in value
    && value.kind === "owner_new_card_media"
    && "schemaVersion" in value
    && value.schemaVersion === NEW_CARD_PHOTO_SCHEMA_VERSION
    && "ownerUserID" in value
    && typeof value.ownerUserID === "string"
    && Boolean(value.ownerUserID.trim())
    && "draftID" in value
    && typeof value.draftID === "string"
    && Boolean(value.draftID.trim())
    && "reference" in value
    && typeof value.reference === "string"
    && "mimeType" in value
    && typeof value.mimeType === "string"
    && "byteCount" in value
    && typeof value.byteCount === "number"
    && "sha256" in value
    && typeof value.sha256 === "string"
    && "image" in value
    && value.image instanceof Blob;
}

function metadataFor(record: StoredNewCardMedia): NewCardPhotoMetadata {
  return {
    schemaVersion: NEW_CARD_PHOTO_SCHEMA_VERSION,
    reference: record.reference,
    mimeType: record.mimeType,
    byteCount: record.byteCount,
    sha256: record.sha256,
  };
}

function assertAllowedImage(image: Blob): void {
  if (
    !(NEW_CARD_IMAGE_MIME_TYPES as readonly string[])
      .includes(image.type.toLowerCase())
    || image.size <= 0
    || image.size > MAX_NEW_CARD_MEDIA_BYTES
  ) {
    throw new Error(
      "Choose a nonempty PNG, JPEG, WebP, GIF, AVIF, HEIC, or HEIF image no larger than 15 MB.",
    );
  }
}

async function assertReadableBrowserImage(image: Blob): Promise<void> {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(image);
      bitmap.close();
      return;
    } catch {
      throw new Error("Choose an image this browser can open.");
    }
  }
  if (typeof Image === "undefined" || typeof URL.createObjectURL !== "function") {
    throw new Error("Private image preview is unavailable in this browser.");
  }
  const objectURL = URL.createObjectURL(image);
  try {
    await new Promise<void>((resolve, reject) => {
      const preview = new Image();
      preview.addEventListener("load", () => resolve(), { once: true });
      preview.addEventListener(
        "error",
        () => reject(new Error("Choose an image this browser can open.")),
        { once: true },
      );
      preview.src = objectURL;
    });
  } finally {
    URL.revokeObjectURL(objectURL);
  }
}

async function sha256Hex(image: Blob): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await image.arrayBuffer());
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

function assertOwnerAndDraft(
  ownerUserID: string,
  draftID: string,
): [string, string] {
  return [
    assertBoundedIdentity(ownerUserID, "owner"),
    assertBoundedIdentity(draftID, "draft"),
  ];
}

function assertBoundedIdentity(
  value: string,
  label: "owner" | "draft",
): string {
  const normalized = value.trim();
  if (
    !normalized
    || normalized.length > 200
    || /[\u0000-\u001f\u007f]/.test(normalized)
  ) {
    throw new Error(`A private photo needs a valid ${label} identity.`);
  }
  return normalized;
}

function cloneStoredMedia(record: StoredNewCardMedia): StoredNewCardMedia {
  return {
    ...record,
    image: cloneBlob(record.image),
  };
}

function cloneBlob(image: Blob): Blob {
  return image.slice(0, image.size, image.type);
}

function indexedDBRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.addEventListener("success", () => resolve(request.result), {
      once: true,
    });
    request.addEventListener("error", () => reject(
      request.error ?? new Error("Private New Card media storage failed."),
    ), { once: true });
  });
}

function indexedDBTransaction(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.addEventListener("complete", () => resolve(), { once: true });
    transaction.addEventListener("abort", () => reject(
      transaction.error ?? new Error("Private New Card media storage failed."),
    ), { once: true });
    transaction.addEventListener("error", () => reject(
      transaction.error ?? new Error("Private New Card media storage failed."),
    ), { once: true });
  });
}
